import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// 10-minute inactivity auto-logout. Server is authoritative; this drives UX + the activity heartbeat.
const IDLE_LIMIT = 600; // seconds
const WARN_AT = 510; // show warning at ~8.5 min idle
const PING_MIN = 60; // throttle server activity pings
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const last = useRef(Date.now());
  const lastPing = useRef(0);
  const loggingOut = useRef(false);
  const [warn, setWarn] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const ping = useCallback(() => {
    const now = Date.now();
    if (now - lastPing.current > PING_MIN * 1000) {
      lastPing.current = now;
      api.post("/auth/activity").catch(() => {});
    }
  }, []);

  const onActivity = useCallback(() => {
    last.current = Date.now();
    setWarn(false);
    ping();
  }, [ping]);

  const doLogout = useCallback(async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    try { await logout(); } catch {}
    window.location.assign("/login?reason=inactivity");
  }, [logout]);

  useEffect(() => {
    if (!user || !user.role) return undefined;
    last.current = Date.now();
    lastPing.current = 0;
    loggingOut.current = false;
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const iv = setInterval(() => {
      const idle = (Date.now() - last.current) / 1000;
      if (idle >= IDLE_LIMIT) doLogout();
      else if (idle >= WARN_AT) { setWarn(true); setRemaining(Math.max(0, Math.ceil(IDLE_LIMIT - idle))); }
    }, 1000);
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(iv);
    };
  }, [user, onActivity, doLogout]);

  if (!user || !user.role || !warn) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[200] flex justify-center p-4" data-testid="idle-warning">
      <div className="flex w-full max-w-md items-center gap-4 rounded-2xl border border-amber-300 bg-white p-4 shadow-2xl">
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-900">Are you still there?</p>
          <p className="text-xs text-slate-500">
            You'll be signed out due to inactivity in{" "}
            <span data-testid="idle-countdown" className="font-semibold text-amber-700">{remaining}s</span>.
          </p>
        </div>
        <Button data-testid="idle-stay-signed-in" size="sm" onClick={onActivity} className="bg-sky-600 text-white hover:bg-sky-700">
          Stay signed in
        </Button>
      </div>
    </div>
  );
}
